/*
 * Purpose: Stage an assembled workflow into a progressive "watch it build" sequence
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import type { Edge, Node, Workflow } from '@/stores/workflowStore';

/** Delay between each staged block insertion during the live build. */
export const LIVE_BUILD_STEP_MS = 280;

/** START first, GOAL second, then operational blocks left-to-right (map reading order). */
export function orderNodesForBuild(workflow: Workflow): Node[] {
  const rank = (n: Node): number => {
    if (n.type === 'start') return 0;
    if (n.type === 'goal') return 1;
    return 2;
  };
  return [...workflow.nodes].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    if (a.x !== b.x) return a.x - b.x;
    return a.y - b.y;
  });
}

export interface LiveBuildStage {
  nodes: Node[];
  edges: Edge[];
}

/**
 * Growing snapshots of the graph — snapshot k contains the first k ordered nodes
 * plus every edge whose endpoints are both already present.
 */
export function stagedGraphs(workflow: Workflow): LiveBuildStage[] {
  const ordered = orderNodesForBuild(workflow);
  const stages: LiveBuildStage[] = [];

  for (let k = 1; k <= ordered.length; k += 1) {
    const nodes = ordered.slice(0, k);
    const present = new Set(nodes.map((n) => n.id));
    const edges = workflow.edges.filter((e) => present.has(e.from) && present.has(e.to));
    stages.push({ nodes, edges });
  }

  return stages;
}

/** Build a partial workflow for a given stage, preserving identity + metadata. */
export function workflowForStage(base: Workflow, stage: LiveBuildStage): Workflow {
  return {
    ...base,
    nodes: stage.nodes,
    edges: stage.edges,
    updatedAt: new Date(),
  };
}
